export const WORK_CONTENT_VERSIONS = new Set([1, 2]);

export type WorkQuestionType =
  | 'multiple_choice'
  | 'long_form'
  | 'short_answer'
  | 'true_false'
  | 'matching'
  | 'file_upload';

export type MatchPair = { left: string; right: string };

export type WorkUploadKind = 'image' | 'video';

export type WorkUploadAnswer = {
  kind: WorkUploadKind;
  url: string;
  fileName?: string;
  fileSize?: number;
  thumbnailUrl?: string;
  videoId?: string;
  uploadedAt?: string;
  status?: 'uploaded' | 'processing';
};

export type WorkQuestion = {
  id: string;
  type: WorkQuestionType;
  prompt: string;
  options: string[];
  answer: string;
  points: number;
  pairs?: MatchPair[];
  /** Optional guidance shown under prompts in student views. */
  instructions?: string;
  /** Optional max character limit for text response prompts. */
  maxChars?: number;
  /** Optional minimum word target for long-form prompts. */
  minWords?: number;
};

export type AssignmentBuilderSettings = {
  shuffleQuestions: boolean;
  allowLateSubmission: boolean;
};

export type ParsedWorkContent = {
  instructions: string;
  questions: WorkQuestion[];
  settings: AssignmentBuilderSettings;
};

const defaultBuilderSettings: AssignmentBuilderSettings = {
  shuffleQuestions: false,
  allowLateSubmission: true,
};

function newQuestionId(): string {
  return `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeQuestionType(raw: unknown): WorkQuestionType {
  if (
    raw === 'long_form' ||
    raw === 'short_answer' ||
    raw === 'true_false' ||
    raw === 'matching' ||
    raw === 'file_upload'
  ) {
    return raw;
  }
  return 'multiple_choice';
}

function parsePairs(raw: unknown): MatchPair[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const pairs = raw
    .map((p) => {
      if (!p || typeof p !== 'object') return null;
      const row = p as Record<string, unknown>;
      return {
        left: typeof row.left === 'string' ? row.left : '',
        right: typeof row.right === 'string' ? row.right : '',
      };
    })
    .filter((row): row is MatchPair => row !== null);
  return pairs.length ? pairs : undefined;
}

function defaultMatchingPairs(): MatchPair[] {
  return [
    { left: '', right: '' },
    { left: '', right: '' },
  ];
}

export function parseWorkDescription(raw: string | null | undefined): ParsedWorkContent {
  if (!raw?.trim()) {
    return { instructions: '', questions: [], settings: { ...defaultBuilderSettings } };
  }

  try {
    const parsed = JSON.parse(raw) as {
      version?: number;
      instructions?: string;
      description?: string;
      body?: string;
      questions?: unknown[];
      items?: unknown[];
      builderSettings?: Partial<AssignmentBuilderSettings>;
      settings?: Partial<AssignmentBuilderSettings>;
    };

    const rows = Array.isArray(parsed.questions)
      ? parsed.questions
      : Array.isArray(parsed.items)
        ? parsed.items
        : [];

    const versionOk =
      WORK_CONTENT_VERSIONS.has(parsed.version ?? -1) ||
      (typeof parsed.version !== 'number' && rows.length > 0);

    if (!versionOk || rows.length === 0) {
      return { instructions: raw, questions: [], settings: { ...defaultBuilderSettings } };
    }

    const questions = rows
      .map((item): WorkQuestion | null => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const type = normalizeQuestionType(row.type);
        const rawOptions = Array.isArray(row.options)
          ? row.options.filter((value): value is string => typeof value === 'string')
          : [];

        const options =
          type === 'true_false'
            ? rawOptions.length >= 2
              ? rawOptions
              : ['True', 'False']
            : type === 'multiple_choice'
              ? rawOptions.length >= 2
                ? rawOptions
                : []
              : [];

        const pairs = type === 'matching' ? parsePairs(row.pairs) : undefined;
        const instructions = typeof row.instructions === 'string' ? row.instructions.trim() : '';
        const maxChars =
          typeof row.maxChars === 'number' && Number.isFinite(row.maxChars) && row.maxChars > 0
            ? Math.min(20000, Math.floor(row.maxChars))
            : undefined;
        const minWords =
          typeof row.minWords === 'number' && Number.isFinite(row.minWords) && row.minWords > 0
            ? Math.floor(row.minWords)
            : undefined;

        return {
          id: typeof row.id === 'string' ? row.id : newQuestionId(),
          type,
          prompt: typeof row.prompt === 'string' ? row.prompt : '',
          options,
          answer: typeof row.answer === 'string' ? row.answer : '',
          points: typeof row.points === 'number' && Number.isFinite(row.points) ? Math.max(1, row.points) : 1,
          pairs: type === 'matching' ? (pairs?.length ? pairs : defaultMatchingPairs()) : undefined,
          instructions: instructions || undefined,
          maxChars,
          minWords,
        };
      })
      .filter((row): row is WorkQuestion => row !== null && row.prompt.trim().length > 0);

    return {
      instructions:
        typeof parsed.instructions === 'string'
          ? parsed.instructions
          : typeof parsed.description === 'string'
            ? parsed.description
            : typeof parsed.body === 'string'
              ? parsed.body
              : '',
      questions,
      settings: {
        ...defaultBuilderSettings,
        ...(parsed.builderSettings ?? {}),
        ...(parsed.settings ?? {}),
      },
    };
  } catch {
    return { instructions: raw, questions: [], settings: { ...defaultBuilderSettings } };
  }
}

export function parseMatchingAnswerMap(raw: string): Record<string, string> {
  if (!raw.trim()) return {};
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, string> = {};
    Object.entries(parsed).forEach(([k, v]) => {
      if (typeof v === 'string' && v.trim()) out[k] = v.trim();
    });
    return out;
  } catch {
    return {};
  }
}

export function parseWorkUploadAnswer(raw: string): WorkUploadAnswer | null {
  const clean = raw.trim();
  if (!clean) return null;

  try {
    const parsed = JSON.parse(clean) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return null;

    const url = typeof parsed.url === 'string' ? parsed.url.trim() : '';
    const kind = parsed.kind === 'video' ? 'video' : parsed.kind === 'image' ? 'image' : null;
    if (!url || !kind) return null;

    return {
      kind,
      url,
      fileName: typeof parsed.fileName === 'string' ? parsed.fileName : undefined,
      fileSize:
        typeof parsed.fileSize === 'number' && Number.isFinite(parsed.fileSize) ? parsed.fileSize : undefined,
      thumbnailUrl: typeof parsed.thumbnailUrl === 'string' ? parsed.thumbnailUrl : undefined,
      videoId: typeof parsed.videoId === 'string' ? parsed.videoId : undefined,
      uploadedAt: typeof parsed.uploadedAt === 'string' ? parsed.uploadedAt : undefined,
      status: parsed.status === 'processing' ? 'processing' : 'uploaded',
    };
  } catch {
    return null;
  }
}

export function isWorkQuestionAnswered(question: WorkQuestion, value: string): boolean {
  const clean = value.trim();
  if (question.type === 'matching') {
    const pairs = question.pairs ?? [];
    if (pairs.length === 0) return false;
    const matchingMap = parseMatchingAnswerMap(clean || '{}');
    return pairs.every((_, idx) => Boolean(matchingMap[String(idx)]));
  }
  if (question.type === 'file_upload') {
    return Boolean(parseWorkUploadAnswer(clean)?.url);
  }
  return clean.length > 0;
}
